import React, { useState, useRef, useEffect, useCallback } from 'react';
import { removeBackground } from '@imgly/background-removal';
import { Link } from 'react-router-dom';
import ThemeSwitcher from './components/ThemeSwitcher';
import MobileMenu from './components/MobileMenu';
import { canvasRGBA } from 'stackblur-canvas';
import './App.css';

// Simple debounce function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Helper function to convert HSL to Hex
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

function App({ darkMode, setDarkMode }) {
  // Image states
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState(null);

  // Configure tab states
  const [borderColor, setBorderColor] = useState('#ffffff');
  const [borderSize, setBorderSize] = useState(15);
  const [shadowColor, setShadowColor] = useState('#000000');

  // Eraser tool states
  const [isEraserActive, setIsEraserActive] = useState(false);
  const [eraserSize, setEraserSize] = useState(20);
  const [isDrawing, setIsDrawing] = useState(false);

  // Color picker modal states
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerType, setColorPickerType] = useState('border'); // 'border' or 'shadow'
  const [customColor, setCustomColor] = useState('#0080ff');
  const [hexInput, setHexInput] = useState('0080ff');

  // Debounce values for performance
  const debouncedBorderColor = useDebounce(borderColor, 100);
  const debouncedBorderSize = useDebounce(borderSize, 100);
  const debouncedShadowColor = useDebounce(shadowColor, 100);

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const displayCanvasRef = useRef(null);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImage(event.target.result);
        setProcessedImage(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const processImage = async () => {
    if (!image) return;

    setIsProcessing(true);
    setProgress(0);
    setError(null);

    try {
      if (isFirstLoad) {
        const progressInterval = setInterval(() => {
          setProgress((prev) => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + 10;
          });
        }, 500);
      }

      // Remove background
      const blob = await removeBackground(image, {
        progress: (key, current, total) => {
          const percentage = Math.round((current / total) * 100);
          setProgress(percentage);
        },
      });

      setIsFirstLoad(false);

      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        setProcessedImage(url);
        setProgress(100);
        setIsProcessing(false);
      };
      img.src = url;
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Failed to process image. Please try again.');
      setIsProcessing(false);
      setProgress(0);
    }
  };

  // Helper to generate sticker on a given canvas with specific scale
  const generateSticker = useCallback((targetCanvas, img, scale = 1) => {
    const ctx = targetCanvas.getContext('2d');

    // Scale parameters
    const currentBorderSize = debouncedBorderSize * scale;
    const currentShadowBlur = debouncedShadowColor ? 10 * scale : 0; // Use default shadow blur if shadow color is set

    // Calculate dimensions
    const width = img.width * scale;
    const height = img.height * scale;
    const totalPadding = 20 * 2 + currentBorderSize * 2; // Use default padding of 20px

    // Use ceil to avoid subpixel clipping
    targetCanvas.width = Math.ceil(width + totalPadding);
    targetCanvas.height = Math.ceil(height + totalPadding);

    // Clear canvas
    ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);

    const centerX = 20 + currentBorderSize;
    const centerY = 20 + currentBorderSize;

    // Create SOLID border using multi-layer technique
    if (currentBorderSize > 0) {
      // Step 1: Create a canvas for the border layer
      const borderCanvas = document.createElement('canvas');
      borderCanvas.width = Math.ceil(width);
      borderCanvas.height = Math.ceil(height);
      const borderCtx = borderCanvas.getContext('2d');

      // Draw the image to get alpha channel
      borderCtx.drawImage(img, 0, 0, width, height);

      // Step 2: Apply MULTIPLE blur passes for solid dilation
      const blurRadius = Math.max(currentBorderSize * 0.6, 1);

      // Apply blur multiple times to build up solid border
      for (let i = 0; i < 3; i++) {
        canvasRGBA(borderCanvas, 0, 0, borderCanvas.width, borderCanvas.height, blurRadius);
      }

      // Step 3: Threshold the alpha to make it SOLID (not transparent)
      const imageData = borderCtx.getImageData(0, 0, borderCanvas.width, borderCanvas.height);
      const data = imageData.data;

      // Create solid color from alpha mask with HARD threshold
      const r = parseInt(debouncedBorderColor.slice(1, 3), 16);
      const g = parseInt(debouncedBorderColor.slice(3, 5), 16);
      const b = parseInt(debouncedBorderColor.slice(5, 7), 16);

      for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        if (alpha > 30) { // Threshold: make semi-transparent pixels solid
          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = 255; // FORCE full opacity for solid border
        } else {
          data[i + 3] = 0; // Remove very faint pixels
        }
      }

      borderCtx.putImageData(imageData, 0, 0);

      // Step 4: Draw border with optional shadow
      if (currentShadowBlur > 0) {
        ctx.shadowColor = debouncedShadowColor;
        ctx.shadowBlur = currentShadowBlur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      ctx.drawImage(borderCanvas, centerX, centerY);

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Step 5: Draw the original image on top
      ctx.drawImage(img, 0, 0, img.width, img.height, centerX, centerY, width, height);
    } else {
      // No border, just draw with optional shadow
      if (currentShadowBlur > 0) {
        ctx.shadowColor = debouncedShadowColor;
        ctx.shadowBlur = currentShadowBlur;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }

      ctx.drawImage(img, 0, 0, img.width, img.height, centerX, centerY, width, height);

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
    }
  }, [debouncedBorderSize, debouncedShadowColor, debouncedBorderColor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const applyEffects = useCallback(() => {
    if (!processedImage) return;

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Preview Scaling: Limit max dimension to e.g. 800px for responsiveness
      const MAX_PREVIEW_SIZE = 800;
      let scale = 1;
      if (img.width > MAX_PREVIEW_SIZE || img.height > MAX_PREVIEW_SIZE) {
        scale = Math.min(MAX_PREVIEW_SIZE / img.width, MAX_PREVIEW_SIZE / img.height);
      }

      const canvas = canvasRef.current;
      generateSticker(canvas, img, scale);
      updateDisplayCanvas();
    };
    img.src = processedImage;
  }, [
    processedImage,
    generateSticker
  ]);

  // Update display when settings change (debounced)
  useEffect(() => {
    if (processedImage) {
      applyEffects();
    }
  }, [processedImage, applyEffects]);

  const updateDisplayCanvas = () => {
    const sourceCanvas = canvasRef.current;
    const displayCanvas = displayCanvasRef.current;
    const eraserCanvas = eraserCanvasRef.current;

    if (!displayCanvas || !sourceCanvas) return;

    const ctx = displayCanvas.getContext('2d');
    displayCanvas.width = sourceCanvas.width;
    displayCanvas.height = sourceCanvas.height;
    ctx.clearRect(0, 0, displayCanvas.width, displayCanvas.height);
    ctx.drawImage(sourceCanvas, 0, 0);

    // Also update eraser canvas if it exists
    if (eraserCanvas) {
      const eraserCtx = eraserCanvas.getContext('2d');
      eraserCanvas.width = sourceCanvas.width;
      eraserCanvas.height = sourceCanvas.height;
      eraserCtx.clearRect(0, 0, eraserCanvas.width, eraserCanvas.height);
      eraserCtx.drawImage(sourceCanvas, 0, 0);
    }
  };

  const downloadImage = (format = 'png') => {
    if (!processedImage) return;

    // Use the eraser canvas if it has modifications, otherwise generate fresh
    const eraserCanvas = eraserCanvasRef.current;

    if (eraserCanvas && eraserCanvas.width > 0) {
      // Download from the eraser canvas (with user modifications)
      let mimeType = 'image/png';
      let extension = 'png';

      if (format === 'webp') {
        mimeType = 'image/webp';
        extension = 'webp';
      } else if (format === 'jpeg') {
        mimeType = 'image/jpeg';
        extension = 'jpg';

        // JPEG background fill
        const jpegCanvas = document.createElement('canvas');
        jpegCanvas.width = eraserCanvas.width;
        jpegCanvas.height = eraserCanvas.height;
        const ctx = jpegCanvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, eraserCanvas.width, eraserCanvas.height);
        ctx.drawImage(eraserCanvas, 0, 0);

        const link = document.createElement('a');
        link.href = jpegCanvas.toDataURL(mimeType);
        link.download = `sticker.${extension}`;
        link.click();
        return;
      }

      const link = document.createElement('a');
      link.href = eraserCanvas.toDataURL(mimeType);
      link.download = `sticker.${extension}`;
      link.click();
      return;
    }

    // Fallback: We need to generate the FULL resolution sticker now
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // Create a temp canvas for full-res export
      const exportCanvas = document.createElement('canvas');
      generateSticker(exportCanvas, img, 1); // scale = 1 for full res

      let mimeType = 'image/png';
      let extension = 'png';

      if (format === 'webp') {
        mimeType = 'image/webp';
        extension = 'webp';
      } else if (format === 'jpeg') {
        mimeType = 'image/jpeg';
        extension = 'jpg';

        // JPEG background fill
        const jpegCanvas = document.createElement('canvas');
        jpegCanvas.width = exportCanvas.width;
        jpegCanvas.height = exportCanvas.height;
        const ctx = jpegCanvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        ctx.drawImage(exportCanvas, 0, 0);

        const link = document.createElement('a');
        link.href = jpegCanvas.toDataURL(mimeType);
        link.download = `sticker.${extension}`;
        link.click();
        return;
      }

      const link = document.createElement('a');
      link.href = exportCanvas.toDataURL(mimeType);
      link.download = `sticker.${extension}`;
      link.click();
    };
    img.src = processedImage;
  };

  const resetApp = () => {
    setImage(null);
    setProcessedImage(null);
    setProgress(0);
    setError(null);
    setIsEraserActive(false);
    setIsDrawing(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleColorPreset = (color, type = 'border') => {
    if (type === 'border') {
      setBorderColor(color);
    } else if (type === 'shadow') {
      setShadowColor(color);
    }
  };

  const openColorPicker = (type = 'border') => {
    setColorPickerType(type);
    const currentColor = type === 'border' ? borderColor : shadowColor;
    setCustomColor(currentColor);
    setHexInput(currentColor.replace('#', ''));
    setShowColorPicker(true);
  };

  const handleColorChange = (color) => {
    setCustomColor(color);
    setHexInput(color.replace('#', ''));
  };

  const handleHexInputChange = (value) => {
    // Remove # if user types it
    const cleanValue = value.replace('#', '');
    setHexInput(cleanValue);

    // Update color if valid hex
    if (/^[0-9A-Fa-f]{6}$/.test(cleanValue)) {
      setCustomColor('#' + cleanValue);
    }
  };

  const applyCustomColor = () => {
    if (/^[0-9A-Fa-f]{6}$/.test(hexInput)) {
      const color = '#' + hexInput;
      if (colorPickerType === 'border') {
        setBorderColor(color);
      } else {
        setShadowColor(color);
      }
      setShowColorPicker(false);
    }
  };

  // Eraser functionality
  const eraserCanvasRef = useRef(null);

  const getCanvasCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const handleEraserStart = (e) => {
    if (!isEraserActive || !processedImage) return;
    setIsDrawing(true);
    const canvas = eraserCanvasRef.current || displayCanvasRef.current;
    const coords = getCanvasCoordinates(e, canvas);
    eraseAtPosition(coords.x, coords.y);
  };

  const handleEraserMove = (e) => {
    if (!isEraserActive || !isDrawing || !processedImage) return;
    e.preventDefault();
    const canvas = eraserCanvasRef.current || displayCanvasRef.current;
    const coords = getCanvasCoordinates(e, canvas);
    eraseAtPosition(coords.x, coords.y);
  };

  const handleEraserEnd = () => {
    setIsDrawing(false);
  };

  const eraseAtPosition = (x, y) => {
    const canvas = eraserCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Use destination-out to erase (makes transparent)
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, eraserSize, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  };

  return (
    <div className={`app ${darkMode ? 'dark' : ''}`}>
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      <canvas ref={displayCanvasRef} style={{ display: 'none' }} />

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div className="color-picker-modal-overlay" onClick={() => setShowColorPicker(false)}>
          <div className="color-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="color-picker-header">
              <h3>Choose Color</h3>
              <button className="close-modal-btn" onClick={() => setShowColorPicker(false)}>✕</button>
            </div>
            <div className="color-picker-content">
              {/* Color Gradient */}
              <div className="color-gradient-container">
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="color-gradient-input"
                />
              </div>

              {/* Current Color Preview */}
              <div className="color-preview-section">
                <div className="color-preview-circle" style={{ backgroundColor: customColor }} />
                <div className="color-gradient-strip">
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={parseInt(customColor.slice(1), 16) % 360}
                    onChange={(e) => {
                      const hue = parseInt(e.target.value);
                      const hex = hslToHex(hue, 100, 50);
                      handleColorChange(hex);
                    }}
                    className="color-hue-slider"
                  />
                </div>
              </div>

              {/* Hex Input */}
              <div className="hex-input-section">
                <div className="hex-input-wrapper">
                  <span className="hex-label">Hex</span>
                  <input
                    type="text"
                    value={hexInput}
                    onChange={(e) => handleHexInputChange(e.target.value)}
                    maxLength={6}
                    className="hex-input"
                    placeholder="0080ff"
                  />
                </div>
              </div>

              {/* Apply Button */}
              <button className="apply-color-btn" onClick={applyCustomColor}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="header-left">
            <Link to="/" className="logo-link">
              <img
                src={darkMode ? '/stickyourslight.png' : '/stickyoursdark.png'}
                alt="Stick Yours"
                className="logo-image"
              />
            </Link>
            <Link to="/" className="home-link">Home</Link>
          </div>
          <div className="header-right">
            <MobileMenu />
            <ThemeSwitcher darkMode={darkMode} setDarkMode={setDarkMode} />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          {/* Hero Section */}
          {!image && !processedImage && (
            <>
              <div className="hero-section">
                <h2 className="hero-title">Create Amazing Stickers with AI</h2>
                <p className="hero-subtitle">
                  Transform any photo into custom stickers in seconds with advanced AI technology
                </p>
                <p className="hero-description">
                  No design skills required. Upload any image and let our AI do the magic—professional borders, transparent backgrounds, and customizable effects in just one click.
                </p>
                <div className="hero-cta-buttons">
                  <button className="btn-hero-primary" onClick={() => fileInputRef.current?.click()}>
                    Create Your Sticker Now
                  </button>
                  <button className="btn-hero-secondary" onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}>
                    See How It Works
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Upload Section */}
          <div className="card">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />

            {!image ? (
              <div className="upload-area" onClick={() => fileInputRef.current.click()}>
                <div className="upload-icon">
                  <img src="/upload.svg" alt="Upload" />
                </div>
                <h3>Click or drag image to upload</h3>
                <p className="upload-hint">PNG, JPG, or WebP • Max 10MB</p>
              </div>
            ) : (
              <div className="image-preview-section">
                <div className="compact-layout">
                  {/* Image Display */}
                  <div className="image-display-area">
                    <div className="image-container">
                      {/* Close Button */}
                      <button className="close-button" onClick={resetApp} title="Close">
                        ✕
                      </button>

                      {/* Display processed or original image */}
                      <div className="main-image-wrapper checkered-bg">
                        {processedImage ? (
                          <canvas
                            ref={eraserCanvasRef}
                            className={`main-image ${isEraserActive ? 'eraser-cursor' : ''}`}
                            style={{
                              cursor: isEraserActive ? 'crosshair' : 'default',
                              touchAction: 'none'
                            }}
                            onMouseDown={handleEraserStart}
                            onMouseMove={handleEraserMove}
                            onMouseUp={handleEraserEnd}
                            onMouseLeave={handleEraserEnd}
                            onTouchStart={handleEraserStart}
                            onTouchMove={handleEraserMove}
                            onTouchEnd={handleEraserEnd}
                          />
                        ) : (
                          <img
                            src={image}
                            alt="Original"
                            className="main-image"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Controls Panel */}
                  <div className="controls-panel">
                    {!isProcessing && !processedImage && (
                      <div className="controls-section">
                        <h3 className="controls-title">Ready to Process</h3>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                          Click "Create Sticker" to remove the background
                        </p>
                      </div>
                    )}

                    {/* Configure Tab */}
                    {processedImage && (
                      <div className="controls-section">
                        {/* Eraser Tool */}
                        <div className="control-group">
                          <label className="control-label">Eraser Tool</label>
                          <button
                            className={`eraser-toggle-btn ${isEraserActive ? 'active' : ''}`}
                            onClick={() => setIsEraserActive(!isEraserActive)}
                          >
                            <span className="eraser-icon">
                              <img src="/eraser.svg" alt="Eraser" style={{ width: '20px', height: '20px' }} />
                            </span>
                            {isEraserActive ? 'Eraser Active' : 'Enable Eraser'}
                          </button>
                          {isEraserActive && (
                            <div className="eraser-controls">
                              <label className="control-label">Eraser Size: {eraserSize}px</label>
                              <input
                                type="range"
                                min="5"
                                max="100"
                                value={eraserSize}
                                onChange={(e) => setEraserSize(Number(e.target.value))}
                                className="slider"
                              />
                              <p className="eraser-hint">Click and drag on the image to erase unwanted areas</p>
                            </div>
                          )}
                        </div>

                        {/* Border Color */}
                        <div className="control-group">
                          <label className="control-label">Border color:</label>
                          <div className="color-presets">
                            <button
                              className={`color-preset ${borderColor === 'transparent' ? 'active' : ''}`}
                              style={{
                                background: 'linear-gradient(45deg, #e5e5e5 25%, transparent 25%, transparent 75%, #e5e5e5 75%, #e5e5e5), linear-gradient(45deg, #e5e5e5 25%, white 25%, white 75%, #e5e5e5 75%, #e5e5e5)',
                                backgroundSize: '12px 12px',
                                backgroundPosition: '0 0, 6px 6px'
                              }}
                              onClick={() => handleColorPreset('transparent', 'border')}
                              title="Transparent"
                            />
                            <button
                              className={`color-preset ${borderColor === '#ffffff' ? 'active' : ''}`}
                              style={{ backgroundColor: '#ffffff', border: '3px solid #e0e0e0' }}
                              onClick={() => handleColorPreset('#ffffff', 'border')}
                              title="White"
                            />
                            <button
                              className={`color-preset ${borderColor === '#000000' ? 'active' : ''}`}
                              style={{ backgroundColor: '#000000' }}
                              onClick={() => handleColorPreset('#000000', 'border')}
                              title="Black"
                            />
                            <button
                              className={`color-preset ${borderColor === '#FFB800' ? 'active' : ''}`}
                              style={{ backgroundColor: '#FFB800' }}
                              onClick={() => handleColorPreset('#FFB800', 'border')}
                              title="Yellow"
                            />
                            <button
                              className={`color-preset ${borderColor === '#4CAF50' ? 'active' : ''}`}
                              style={{ backgroundColor: '#4CAF50' }}
                              onClick={() => handleColorPreset('#4CAF50', 'border')}
                              title="Green"
                            />
                            <button
                              className={`color-preset ${borderColor === '#9C27B0' ? 'active' : ''}`}
                              style={{ backgroundColor: '#9C27B0' }}
                              onClick={() => handleColorPreset('#9C27B0', 'border')}
                              title="Purple"
                            />
                            <button
                              className={`color-preset ${borderColor === '#2196F3' ? 'active' : ''}`}
                              style={{ backgroundColor: '#2196F3' }}
                              onClick={() => handleColorPreset('#2196F3', 'border')}
                              title="Blue"
                            />
                            <button
                              className={`color-preset ${borderColor === '#FF5252' ? 'active' : ''}`}
                              style={{ backgroundColor: '#FF5252' }}
                              onClick={() => handleColorPreset('#FF5252', 'border')}
                              title="Red"
                            />
                            <button
                              className="color-preset rainbow"
                              style={{ background: 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3)' }}
                              onClick={() => openColorPicker('border')}
                              title="Custom"
                            />
                          </div>
                        </div>

                        {/* Border Size */}
                        <div className="control-group">
                          <label className="control-label">Border size:</label>
                          <input
                            type="range"
                            min="0"
                            max="50"
                            value={borderSize}
                            onChange={(e) => setBorderSize(Number(e.target.value))}
                            className="slider"
                          />
                        </div>

                        {/* Shadow Color */}
                        <div className="control-group">
                          <label className="control-label">Shadow color:</label>
                          <div className="color-presets">
                            <button
                              className={`color-preset ${shadowColor === 'transparent' ? 'active' : ''}`}
                              style={{
                                background: 'linear-gradient(45deg, #e5e5e5 25%, transparent 25%, transparent 75%, #e5e5e5 75%, #e5e5e5), linear-gradient(45deg, #e5e5e5 25%, white 25%, white 75%, #e5e5e5 75%, #e5e5e5)',
                                backgroundSize: '12px 12px',
                                backgroundPosition: '0 0, 6px 6px'
                              }}
                              onClick={() => handleColorPreset('transparent', 'shadow')}
                              title="Transparent"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#ffffff' ? 'active' : ''}`}
                              style={{ backgroundColor: '#ffffff', border: '3px solid #e0e0e0' }}
                              onClick={() => handleColorPreset('#ffffff', 'shadow')}
                              title="White"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#000000' ? 'active' : ''}`}
                              style={{ backgroundColor: '#000000' }}
                              onClick={() => handleColorPreset('#000000', 'shadow')}
                              title="Black"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#FFB800' ? 'active' : ''}`}
                              style={{ backgroundColor: '#FFB800' }}
                              onClick={() => handleColorPreset('#FFB800', 'shadow')}
                              title="Yellow"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#4CAF50' ? 'active' : ''}`}
                              style={{ backgroundColor: '#4CAF50' }}
                              onClick={() => handleColorPreset('#4CAF50', 'shadow')}
                              title="Green"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#9C27B0' ? 'active' : ''}`}
                              style={{ backgroundColor: '#9C27B0' }}
                              onClick={() => handleColorPreset('#9C27B0', 'shadow')}
                              title="Purple"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#2196F3' ? 'active' : ''}`}
                              style={{ backgroundColor: '#2196F3' }}
                              onClick={() => handleColorPreset('#2196F3', 'shadow')}
                              title="Blue"
                            />
                            <button
                              className={`color-preset ${shadowColor === '#FF5252' ? 'active' : ''}`}
                              style={{ backgroundColor: '#FF5252' }}
                              onClick={() => handleColorPreset('#FF5252', 'shadow')}
                              title="Red"
                            />
                            <button
                              className="color-preset rainbow"
                              style={{ background: 'linear-gradient(135deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3)' }}
                              onClick={() => openColorPicker('shadow')}
                              title="Custom"
                            />
                          </div>
                        </div>

                        {/* Download Options */}
                        <div className="control-group">
                          <label className="control-label">Download your image as:</label>
                          <div className="download-buttons">
                            <button className="download-btn" onClick={() => downloadImage('png')}>
                              <span>PNG</span>
                            </button>
                            <button className="download-btn" onClick={() => downloadImage('webp')}>
                              <span>WEBP</span>
                            </button>
                            <button className="download-btn" onClick={() => downloadImage('jpeg')}>
                              <span>JPEG</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Progress Bar */}
                    {isProcessing && (
                      <div className="progress-section">
                        <div className="progress-bar-container">
                          <div
                            className="progress-bar"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="progress-text">{progress}%</p>
                        {isFirstLoad && progress < 50 && (
                          <p className="progress-hint">
                            📥 Downloading AI model
                          </p>
                        )}
                        {progress >= 50 && (
                          <p className="progress-hint">
                            ✨ Processing...
                          </p>
                        )}
                      </div>
                    )}

                    {/* Error Message */}
                    {error && (
                      <div className="error-message">
                        <span className="error-icon">⚠️</span>
                        {error}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="button-group">
                      {!processedImage && !isProcessing && (
                        <button
                          className="btn btn-primary"
                          onClick={processImage}
                        >
                          Create Sticker
                        </button>
                      )}

                      {processedImage && (
                        <button
                          className="btn btn-secondary"
                          onClick={resetApp}
                        >
                          New Sticker
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* How It Works Section */}
          {!image && !processedImage && (
            <div className="steps-section" id="how-it-works">
              <h2 className="steps-title">Create Your Sticker in Three Simple Steps</h2>
              <div className="steps-grid">
                <div className="step-card">
                  <div className="step-number">1</div>
                  <h3>Upload Your Image</h3>
                  <p>Drag and drop or click to select any photo—selfies, pets, logos, or objects. We support PNG, JPG, WEBP, GIF, and more.</p>
                </div>
                <div className="step-card">
                  <div className="step-number">2</div>
                  <h3>AI Creates Your Sticker</h3>
                  <p>Our advanced AI instantly removes the background and adds a professional border. Watch the magic happen in seconds—no editing required!</p>
                </div>
                <div className="step-card">
                  <div className="step-number">3</div>
                  <h3>Customize & Download</h3>
                  <p>Adjust border colors, shadow effects, and padding to match your style. Download your sticker as PNG, WEBP, or JPEG with transparent background.</p>
                </div>
              </div>
            </div>
          )}

          {/* Use Cases Section */}
          {!image && !processedImage && (
            <div className="use-cases-section">
              <h2 className="section-title">Perfect for Every Occasion</h2>
              <div className="use-cases-grid">
                <div className="use-case-card">
                  <h3>Spice Up Your Chats</h3>
                  <p>Create personalized stickers for WhatsApp, Telegram, Instagram, Snapchat, and Facebook Messenger. Make your conversations unique and memorable.</p>
                </div>
                <div className="use-case-card">
                  <h3>Professional Branding Made Easy</h3>
                  <p>Transform your logo, profile photo, or product images into eye-catching stickers for marketing materials and social media.</p>
                </div>
                <div className="use-case-card">
                  <h3>Unleash Your Creativity</h3>
                  <p>Design custom stickers for digital art, scrapbooking, presentations, or any creative project. Perfect for artists and designers.</p>
                </div>
                <div className="use-case-card">
                  <h3>Print-Ready Designs</h3>
                  <p>Prepare your images for print-on-demand products, custom merchandise, or personalized gifts with professional-quality output.</p>
                </div>
              </div>
            </div>
          )}

          {/* Features Section */}
          {!image && !processedImage && (
            <div className="features-section">
              <h2 className="section-title">Powerful Features, Simple to Use</h2>
              <div className="features-grid">
                <div className="feature-item">
                  <h4>AI-Powered Background Removal</h4>
                  <p>Intelligent edge detection removes backgrounds automatically with precision</p>
                </div>
                <div className="feature-item">
                  <h4>Customizable Borders</h4>
                  <p>Choose from multiple border styles, colors, and thickness options</p>
                </div>
                <div className="feature-item">
                  <h4>Professional Shadow Effects</h4>
                  <p>Add depth with adjustable shadow blur, color, and opacity</p>
                </div>
                <div className="feature-item">
                  <h4>Multiple Export Formats</h4>
                  <p>Download as PNG, WEBP, or JPEG optimized for any platform</p>
                </div>
                <div className="feature-item">
                  <h4>Transparent Backgrounds</h4>
                  <p>Perfect PNG output ready for any use case</p>
                </div>
                <div className="feature-item">
                  <h4>Instant Results</h4>
                  <p>Get your sticker in seconds, not minutes</p>
                </div>
                <div className="feature-item">
                  <h4>No Design Skills Needed</h4>
                  <p>User-friendly interface anyone can master</p>
                </div>
                <div className="feature-item">
                  <h4>Full Customization Control</h4>
                  <p>Fine-tune every aspect of your sticker</p>
                </div>
              </div>
            </div>
          )}

          {/* FAQ Section */}
          {!image && !processedImage && (
            <div className="faq-section">
              <h2 className="section-title">Frequently Asked Questions</h2>
              <div className="faq-grid">
                <div className="faq-item">
                  <h4>What image formats do you support?</h4>
                  <p>We support PNG, JPG, JPEG, WEBP, GIF, BMP, and AVIF formats. Upload any standard image file and we'll handle the rest.</p>
                </div>
                <div className="faq-item">
                  <h4>Is my data private and secure?</h4>
                  <p>Yes! All image processing happens locally in your browser. Your images never leave your device—100% client-side processing.</p>
                </div>
                <div className="faq-item">
                  <h4>Can I use these stickers commercially?</h4>
                  <p>Yes, you retain full rights to your created stickers and can use them for personal or commercial purposes.</p>
                </div>
                <div className="faq-item">
                  <h4>What's the maximum image size?</h4>
                  <p>For optimal performance, we recommend images up to 10MB, though larger images are supported.</p>
                </div>
                <div className="faq-item">
                  <h4>Do I need to create an account?</h4>
                  <p>No account needed! Start creating stickers immediately—completely free.</p>
                </div>
                <div className="faq-item">
                  <h4>Can I edit the sticker after downloading?</h4>
                  <p>Yes! Download your sticker and upload it again to make further customizations anytime.</p>
                </div>
              </div>
            </div>
          )}

          {/* Final CTA Section */}
          {!image && !processedImage && (
            <div className="final-cta-section">
              <h2 className="final-cta-title">Ready to Create Your Perfect Sticker?</h2>
              <p className="final-cta-description">
                Join thousands of creators transforming their images into stunning stickers. Fast, free, and incredibly easy.
              </p>
              <button className="btn-final-cta" onClick={() => fileInputRef.current?.click()}>
                Get Started Now
              </button>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-links">
            <Link to="/about" className="footer-link">About Us</Link>
            <Link to="/cont
            act" className="footer-link">Contact Us</Link>
            <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            <Link to="/terms" className="footer-link">Terms & Conditions</Link>
            <Link to="/disclaimer" className="footer-link">Disclaimer</Link>
          </div>
          <p className="footer-text">
            100% Client-Side Processing • Your images never leave your device
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
